import dayjs from 'dayjs';
import 'dayjs/locale/da';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/fy';
import 'dayjs/locale/it';
import 'dayjs/locale/nl';
import 'dayjs/locale/sv';

let locale: string = 'en';
export type MomentFallbackDate = Date & {
    getDateObject: () => Date;
    toDate: () => Date;
    isValid: () => boolean;
    format: (dateFormat: string) => string;
    add: (amount: number, unit: string) => MomentFallbackDate;
    subtract: (amount: number, unit: string) => MomentFallbackDate;
    isBefore: (date: Date) => boolean;
    isAfter: (date: Date) => boolean;
    isSame: (date: Date) => boolean;
    diff: (date: Date | number | string | undefined | null, unit: string) => number;
    clone: () => MomentFallbackDate;
    endOf: (unit: string) => MomentFallbackDate;
};

export function convertFormat(dateFormat: string): string {
    // ignore month without leading zero, we fix it in parsing later.
    if ('YYYY-M-DD' === dateFormat || 'yyyy-M-dd' === dateFormat) {
        dateFormat = 'YYYY-MM-DD';
    }

    const tokenMap: Record<string, string> = {
        yyyy: 'YYYY',
        yy: 'YY',
        EEEE: 'dddd',
        EEE: 'ddd',
        EEEEE: 'dd',
        EEEEEE: 'dd',
        LLL: 'MMM',
        LLLL: 'MMMM',
        dd: 'DD',
        d: 'D',
    };

    let result = '';
    let i = 0;

    while (i < dateFormat.length) {
        const char = dateFormat[i];
        if (!char) {
            i++;
            continue;
        }

        if (/[A-Za-z]/.test(char)) {
            let j = i + 1;
            while (j < dateFormat.length && dateFormat[j] === char) {
                j++;
            }
            const token = dateFormat.slice(i, j);

            // Only convert tokens we explicitly recognize; leave others untouched.
            result += tokenMap[token] ?? token;
            i = j;
            continue;
        }

        result += char;
        i++;
    }

    return result;
}

export const convertAnyToDate = (
    input: Date | number | string | undefined | null = undefined,
    dateFormat: string = 'YYYY-MM-DD',
): Date => {
    let dte: Date | any;
    const normalizedFormat = convertFormat(dateFormat);

    if (input instanceof Date) {
        (input as Date).setMilliseconds(0);

        return input as Date;
    }

    if (typeof input === 'undefined' || null === input) {
        dte = new Date();
    } else if (typeof input === 'string') {
        const split = input.split('-');
        if (split.length === 3) {
            for (let i = 0; i < split.length; i++) {
                const part = split[i];
                if (!part) {
                    continue;
                }
                if (part.length === 1) {
                    split[i] = '0' + part;
                }
            }
            input = split.join('-');
        }

        if (normalizedFormat === 'YYYY-MM-DD') {
            dte = new Date(input);
        } else if (normalizedFormat === 'YYYYMMDD') {
            dte = new Date(input.substring(0, 4) + '-' + input.substring(4, 6) + '-' + input.substring(6, 8));
        } else if (normalizedFormat === 'DD-MM-YYYY') {
            dte = new Date(input.substring(6, 10) + '-' + input.substring(3, 5) + '-' + input.substring(0, 2));
        }
    } else if (typeof input === 'number') {
        dte = new Date(input);
    } else {
        dte = new Date(input);
    }

    dte.setMilliseconds(0);

    return dte;
};

/**
 * This creates a fallback function to imitate the momentjs api for date manipulation.
 */
function moment(
    date: Date | number | string | undefined | null = undefined,
    dateFormat: string = 'YYYY-MM-DD',
): MomentFallbackDate {
    dateFormat = convertFormat(dateFormat);
    const dte = convertAnyToDate(date, dateFormat) as Date | MomentFallbackDate;
    const dateClone = new Date(dte.getTime());

    (dte as any).getDateObject = (): Date => {
        return dateClone;
    };
    (dte as any).isValid = (): boolean => {
        return dayjs(dte).isValid();
    };
    (dte as any).toDate = (dte as MomentFallbackDate).getDateObject;
    (dte as any).format = (dateFormat: string = 'YYYY-MM-DD'): string => {
        const convertedFormat = convertFormat(dateFormat);
        const dateClone = (dte as MomentFallbackDate).clone();

        return dayjs(dateClone).locale(locale).format(convertedFormat);
    };
    (dte as any).add = (amount: number, unit: string) => {
        const clone = new Date(dte.getTime());

        if ('seconds' === unit || 'second' === unit) {
            clone.setSeconds(clone.getSeconds() + amount);

            return moment(clone);
        }

        if ('minutes' === unit || 'minute' === unit) {
            clone.setMinutes(clone.getMinutes() + amount);

            return moment(clone);
        }

        if ('hours' === unit || 'hour' === unit) {
            clone.setHours(clone.getHours() + amount);

            return moment(clone);
        }

        if ('days' === unit || 'day' === unit) {
            clone.setDate(clone.getDate() + amount);

            return moment(clone);
        }

        if ('months' === unit || 'month' === unit) {
            clone.setMonth(clone.getMonth() + amount);

            return moment(clone);
        }

        if ('years' === unit || 'year' === unit) {
            clone.setFullYear(clone.getFullYear() + amount);

            return moment(clone);
        }

        if ('week' === unit || 'weeks' === unit) {
            clone.setDate(clone.getDate() + 7 * amount);

            return moment(clone);
        }

        throw new Error('Invalid unit ' + unit);
    };
    (dte as any).subtract = (amount: number, unit: string) => {
        const clone = new Date(dte.getTime());

        if ('seconds' === unit || 'second' === unit) {
            clone.setSeconds(clone.getSeconds() - amount);

            return moment(clone);
        }

        if ('minutes' === unit || 'minute' === unit) {
            clone.setMinutes(clone.getMinutes() - amount);

            return moment(clone);
        }

        if ('hours' === unit || 'hour' === unit) {
            clone.setHours(clone.getHours() - amount);

            return moment(clone);
        }

        if ('days' === unit || 'day' === unit) {
            clone.setDate(clone.getDate() - amount);

            return moment(clone);
        }

        if ('months' === unit || 'month' === unit) {
            clone.setMonth(clone.getMonth() - amount);

            return moment(clone);
        }

        if ('years' === unit || 'year' === unit) {
            clone.setFullYear(clone.getFullYear() - amount);

            return moment(clone);
        }

        if ('week' === unit || 'weeks' === unit) {
            clone.setDate(clone.getDate() - 7 * amount);

            return moment(clone);
        }

        throw new Error('Invalid unit ' + unit);
    };
    (dte as any).isBefore = (date: Date) => {
        return dayjs(dte).isBefore(dayjs(date));
    };
    (dte as any).isAfter = (date: Date) => {
        return dayjs(dte).isAfter(dayjs(date));
    };
    (dte as any).isSame = (date: Date) => {
        return (
            dte.getFullYear() === date.getFullYear() &&
            dte.getMonth() === date.getMonth() &&
            dte.getDate() === date.getDate() &&
            dte.getHours() === date.getHours() &&
            dte.getMinutes() === date.getMinutes() &&
            dte.getSeconds() === date.getSeconds()
        );
    };
    (dte as any).diff = (date: any, unit: string) => {
        const clone = new Date(dte.getTime());
        date = convertAnyToDate(date);

        if ('seconds' === unit || 'second' === unit) {
            return Math.floor(Math.abs(clone.getTime() - date.getTime()) / 1000);
        }

        if ('minutes' === unit || 'minute' === unit) {
            return Math.floor(Math.abs(clone.getTime() - date.getTime()) / 1000 / 60);
        }

        if ('hours' === unit || 'hour' === unit) {
            return Math.floor(Math.abs(clone.getTime() - date.getTime()) / 1000 / 60 / 60);
        }

        if ('days' === unit || 'day' === unit) {
            return Math.floor(Math.abs(clone.getTime() - date.getTime()) / 1000 / 60 / 60 / 24);
        }

        if ('months' === unit || 'month' === unit) {
            return Math.floor(Math.abs(clone.getMonth() - date.getMonth()));
        }

        if ('years' === unit || 'year' === unit) {
            return Math.floor(Math.abs(clone.getFullYear() - date.getFullYear()));
        }

        if ('week' === unit || 'weeks' === unit) {
            return Math.floor(Math.abs(clone.getTime() - date.getTime()) / 1000 / 60 / 60 / 24 / 7);
        }

        throw new Error('Invalid unit ' + unit);
    };
    (dte as any).clone = () => {
        return moment((dte as MomentFallbackDate).getDateObject());
    };
    (dte as any).endOf = (unit: string) => {
        if ('day' === unit || 'days' === unit) {
            return moment(new Date(dte).setHours(23, 59, 59, 0));
        }

        if ('month' === unit || 'months' === unit) {
            return moment(new Date(dte.getFullYear(), dte.getMonth() + 1, 0).setHours(23, 59, 59, 0));
        }

        if ('year' === unit || 'years' === unit) {
            return moment(new Date(dte.getFullYear(), 11, 31).setHours(23, 59, 59, 0));
        }

        if ('hour' === unit || 'hours' === unit) {
            return moment(new Date(dte).setMinutes(59, 59, 0));
        }

        if ('minute' === unit || 'minutes' === unit) {
            return moment(new Date(dte).setSeconds(59, 0));
        }

        if ('second' === unit || 'seconds' === unit) {
            return moment(new Date(dte).setMilliseconds(999));
        }

        if ('week' === unit || 'weeks' === unit) {
            return moment(new Date(dte).setDate(dte.getDate() + 6 - dte.getDay()));
        }

        throw new Error('Invalid unit ' + unit);
    };

    return dte as MomentFallbackDate;
}
/**
 * Global moment locale compatibility function
 */
moment.locale = (newLocale?: string) => {
    if (!newLocale) {
        return locale;
    }

    switch (newLocale) {
        case 'nl':
            locale = 'nl';
            break;
        case 'de':
            locale = 'de';
            break;
        case 'fr':
            locale = 'fr';
            break;
        case 'it':
            locale = 'it';
            break;
        case 'fy':
        case 'fy_NL':
            locale = 'fy';
            break;
        case 'sv':
            locale = 'sv';
            break;
        case 'da':
            locale = 'da';
            break;
        case 'es':
            locale = 'es';
            break;
        default:
        case 'en':
        case 'en-US':
        case 'en_US':
        case 'enUS':
            locale = 'en';
            break;
    }

    dayjs.locale(locale);
};

/**
 * Global moment unix compatibility function
 */
moment.unix = (timestamp: number) => {
    return moment(new Date(timestamp * 1000));
};

moment.isDate = (date: any) => {
    return date instanceof Date;
};

export default moment;
